// @spec GHG-STR-013
'use client';

/**
 * GHG Integration Connector detail page.
 * Shows connector details, field mappings, and sync history.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Edit } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useIntegrationConnector,
  useSyncRuns,
  useTriggerSync,
} from '@/features/ghg/api/use-integration-connectors';
import { ConnectorSyncStatus, IntegrationConnectorForm } from '@/features/ghg/components';
import { useGHGRole } from '@/features/ghg/hooks/useGHGRole';
import type { ConnectorStatus, GHGIntegrationType, SyncRunStatus } from '@/features/ghg/types';
import i18n from '@/lib/i18n';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_authenticated/ghg/integrations/$connectorId')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.connectorDetails'),
  }),
  component: ConnectorDetailPage,
});

/**
 * Format integration type for display
 */
function getIntegrationTypeLabels(t: (key: string) => string): Record<GHGIntegrationType, string> {
  return {
    utility_api: t('connectorDetailPage.integrationType_utilityApi'),
    fleet_telematics: t('connectorDetailPage.integrationType_fleetTelematics'),
    travel_booking: t('connectorDetailPage.integrationType_travelBooking'),
  };
}

function formatIntegrationType(type: GHGIntegrationType, t: (key: string) => string): string {
  const labels = getIntegrationTypeLabels(t);
  return labels[type] || type;
}

/**
 * Get status badge classes
 */
function getStatusClasses(status: ConnectorStatus): string {
  switch (status) {
    case 'connected':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    case 'configured':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
    case 'error':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
    case 'disabled':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  }
}

/**
 * Get sync run status badge classes
 */
function getSyncStatusClasses(status: SyncRunStatus): string {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    case 'completed_with_errors':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
    case 'running':
    case 'pending':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    case 'failed':
    case 'cancelled':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  }
}

/**
 * Format date for display
 */
function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function ConnectorDetailPage() {
  const navigate = useNavigate();

  const { connectorId } = Route.useParams();
  const { isManager: isGHGManager } = useGHGRole();
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const { connector, isLoading, isError, error } = useIntegrationConnector(connectorId);

  const { syncRuns, isLoading: syncRunsLoading } = useSyncRuns(connectorId);
  const triggerSyncMutation = useTriggerSync();

  const handleBack = () => {
    void navigate({ to: '/ghg/integrations' });
  };

  const handleEdit = () => {
    setEditDialogOpen(true);
  };

  const handleEditSuccess = () => {
    setEditDialogOpen(false);
  };

  const handleTriggerSync = async () => {
    if (!connector) return;

    try {
      await triggerSyncMutation.mutateAsync({
        connectorId: connector.id,
        data: { dryRun: false },
      });
      toast.success(t('toast.syncStarted'), {
        description: t('connectorDetailPage.syncJobQueued'),
      });
    } catch {
      toast.error(t('ghg:toast.failedToTriggerSync'));
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-5xl space-y-6">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-5xl">
          <Alert variant="destructive">
            <AlertTitle>{t('connectorDetailPage.errorLoading')}</AlertTitle>
            <AlertDescription>
              {error?.message ?? t('connectorDetailPage.failedToLoadDetails')}
            </AlertDescription>
          </Alert>
          <Button variant="outline" className="mt-4" onClick={handleBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('connectorDetailPage.backToConnectors')}
          </Button>
        </div>
      </div>
    );
  }

  // Not found
  if (!connector) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-5xl">
          <Alert>
            <AlertTitle>{t('connectorDetailPage.notFound')}</AlertTitle>
            <AlertDescription>{t('connectorDetailPage.notFoundDesc')}</AlertDescription>
          </Alert>
          <Button variant="outline" className="mt-4" onClick={handleBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('connectorDetailPage.backToConnectors')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-5xl space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <Button variant="ghost" size="sm" className="-ml-2" onClick={handleBack}>
              <ArrowLeft className="mr-1 h-4 w-4" />
              {t('connectorDetailPage.back')}
            </Button>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold">{connector.name}</h1>
              <Badge className={cn('capitalize', getStatusClasses(connector.status))}>
                {connector.status}
              </Badge>
            </div>
            {connector.description && (
              <p className="text-muted-foreground">{connector.description}</p>
            )}
          </div>

          {isGHGManager && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleEdit}>
                <Edit className="mr-2 h-4 w-4" />
                {t('connectorDetailPage.edit')}
              </Button>
            </div>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Connector Info */}
            <Card>
              <CardHeader>
                <CardTitle>{t('connectorDetailPage.configuration')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('connectorDetailPage.integrationType')}</p>
                    <p className="mt-1">{formatIntegrationType(connector.integrationType, t)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('connectorDetailPage.syncFrequency')}</p>
                    <p className="mt-1 capitalize">{connector.syncFrequency}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {t('connectorDetailPage.defaultDataQuality')}
                    </p>
                    <p className="mt-1 capitalize">{connector.defaultDataQuality}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('connectorDetailPage.defaultFacility')}</p>
                    <p className="mt-1">
                      {connector.defaultFacility?.name ?? connector.defaultFacilityId ?? '-'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Field Mappings */}
            <Card>
              <CardHeader>
                <CardTitle>{t('connectorDetailPage.fieldMappings')}</CardTitle>
                <CardDescription>
                  {t('connectorDetailPage.fieldMappingsDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {connector.fieldMappings.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('connectorDetailPage.externalField')}</TableHead>
                        <TableHead>{t('connectorDetailPage.scope')}</TableHead>
                        <TableHead>{t('connectorDetailPage.category')}</TableHead>
                        <TableHead>{t('connectorDetailPage.type')}</TableHead>
                        <TableHead>{t('connectorDetailPage.group')}</TableHead>
                        <TableHead>{t('connectorDetailPage.transform')}</TableHead>
                        <TableHead>{t('connectorDetailPage.unit')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {connector.fieldMappings.map((mapping) => (
                        <TableRow key={mapping.id}>
                          <TableCell className="font-mono text-sm">
                            {mapping.externalField}
                          </TableCell>
                          <TableCell>{`Scope ${String(mapping.scope)}`}</TableCell>
                          <TableCell>{mapping.activityCategory}</TableCell>
                          <TableCell>{mapping.activityType}</TableCell>
                          <TableCell>{mapping.activityGroup?.name ?? '-'}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {mapping.valueTransform ?? '-'}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {mapping.unitOverride ?? t('connectorDetailPage.default')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('connectorDetailPage.noFieldMappings')}</p>
                )}
              </CardContent>
            </Card>

            {/* Sync History */}
            <Card>
              <CardHeader>
                <CardTitle>{t('connectorDetailPage.syncHistory')}</CardTitle>
                <CardDescription>{t('connectorDetailPage.syncHistoryDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                {syncRunsLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : syncRuns.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('connectorDetailPage.started')}</TableHead>
                        <TableHead>{t('connectorDetailPage.status')}</TableHead>
                        <TableHead>{t('connectorDetailPage.trigger')}</TableHead>
                        <TableHead>{t('connectorDetailPage.records')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {syncRuns.map((run) => (
                        <TableRow key={run.id}>
                          <TableCell>{formatDate(run.startedAt)}</TableCell>
                          <TableCell>
                            <Badge className={cn('capitalize', getSyncStatusClasses(run.status))}>
                              {run.status.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="capitalize text-muted-foreground">
                            {run.triggerSource}
                          </TableCell>
                          <TableCell>
                            {typeof run.summary.recordsProcessed === 'number'
                              ? t('connectorDetailPage.recordsProcessed', { count: run.summary.recordsProcessed })
                              : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('connectorDetailPage.noSyncRuns')}</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <ConnectorSyncStatus
              connectorId={connector.id}
              status={connector.status}
              lastSyncAt={connector.lastSyncAt}
              nextSyncAt={connector.nextSyncAt}
              onTriggerSync={() => {
                void handleTriggerSync();
              }}
              isSyncing={triggerSyncMutation.isPending}
            />

            {/* API Config Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{t('connectorDetailPage.apiConfiguration')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">{t('connectorDetailPage.endpointPath')}</p>
                  <p className="mt-0.5 font-mono text-sm break-all">
                    {connector.apiConfig.endpointPath}
                  </p>
                </div>
                {connector.apiConfig.responseDataPath && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">{t('connectorDetailPage.responseDataPath')}</p>
                    <p className="mt-0.5 font-mono text-sm">
                      {connector.apiConfig.responseDataPath}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('integrationsPage.editDialogTitle')}</DialogTitle>
            <DialogDescription>
              {t('integrationsPage.editDialogDesc')}
            </DialogDescription>
          </DialogHeader>
          <IntegrationConnectorForm
            connector={connector}
            onSuccess={handleEditSuccess}
            onCancel={() => {
              setEditDialogOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
